import React, { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { Combobox } from "@/components/ui/combobox";
import { SelectOptionItem } from "@/lib/models/ui";
import { Loader, Download } from "lucide-react";
import moment from "moment-timezone";
import { CommonDestinationTimezone } from "@/lib/utils";
import { downloadFromS3 } from "@/lib/file";
import {
    useCreateDailyReconReport,
    useCreatePnlReport,
    useCreateEquityReport,
    useCreateNetVolumeReport
} from "@/lib/api/brokerage";

type ReportType = "daily_recon" | "pnl" | "equity" | "net_volume";

interface ReportGenerationModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export function ReportGenerationModal({ isOpen, onClose }: ReportGenerationModalProps) {
    const { toast } = useToast();
    const today = moment.tz(CommonDestinationTimezone);
    
    const [reportType, setReportType] = useState<ReportType>("daily_recon");
    const [startDate, setStartDate] = useState<string>(today.clone().startOf("month").format("YYYY-MM-DD"));
    const [endDate, setEndDate] = useState<string>(today.clone().endOf("month").format("YYYY-MM-DD"));
    const [selectedMonth, setSelectedMonth] = useState<string>(today.format("YYYY-MM"));
    const [isGenerating, setIsGenerating] = useState(false);
    
    const createDailyReconReport = useCreateDailyReconReport();
    const createPnlReport = useCreatePnlReport();
    const createEquityReport = useCreateEquityReport();
    const createNetVolumeReport = useCreateNetVolumeReport();
    
    const reportTypeOptions: SelectOptionItem<ReportType>[] = [
        { value: "daily_recon", label: "Daily Reconciliation" },
        { value: "pnl", label: "P&L Report" },
        { value: "equity", label: "Equity Report" },
        { value: "net_volume", label: "Net Volume Report" }
    ];
    
    const isMonthlyReport = reportType !== "net_volume";
    
    const monthPresets = [
        {
            label: "Current Month",
            getValue: () => today.format("YYYY-MM")
        },
        {
            label: "Previous Month",
            getValue: () => today.clone().subtract(1, "month").format("YYYY-MM")
        },
        {
            label: "2 Months Ago",
            getValue: () => today.clone().subtract(2, "months").format("YYYY-MM")
        },
        {
            label: "3 Months Ago",
            getValue: () => today.clone().subtract(3, "months").format("YYYY-MM")
        }
    ];
    
    const dateRangePresets = [
        {
            label: "Current Month",
            getValue: () => ({
                start: today.clone().startOf("month").format("YYYY-MM-DD"),
                end: today.clone().endOf("month").format("YYYY-MM-DD")
            })
        },
        {
            label: "Last 3 Months",
            getValue: () => ({
                start: today.clone().subtract(3, "months").startOf("month").format("YYYY-MM-DD"),
                end: today.clone().endOf("month").format("YYYY-MM-DD")
            })
        },
        {
            label: "Last 6 Months",
            getValue: () => ({
                start: today.clone().subtract(6, "months").startOf("month").format("YYYY-MM-DD"),
                end: today.clone().endOf("month").format("YYYY-MM-DD")
            })
        },
        {
            label: "Year to Date",
            getValue: () => ({
                start: today.clone().startOf("year").format("YYYY-MM-DD"),
                end: today.clone().endOf("month").format("YYYY-MM-DD")
            })
        }
    ];
    
    const effectiveStartDate = useMemo(() => {
        if (isMonthlyReport && selectedMonth) {
            return moment(selectedMonth, "YYYY-MM").startOf("month").format("YYYY-MM-DD");
        }
        return startDate;
    }, [isMonthlyReport, selectedMonth, startDate]);
    
    const effectiveEndDate = useMemo(() => {
        if (isMonthlyReport && selectedMonth) {
            return moment(selectedMonth, "YYYY-MM").endOf("month").format("YYYY-MM-DD");
        }
        return endDate;
    }, [isMonthlyReport, selectedMonth, endDate]);
    
    const handleMonthChange = (monthValue: string) => {
        setSelectedMonth(monthValue);
        const monthMoment = moment(monthValue, "YYYY-MM");
        setStartDate(monthMoment.clone().startOf("month").format("YYYY-MM-DD"));
        setEndDate(monthMoment.clone().endOf("month").format("YYYY-MM-DD"));
    };
    
    const handleDateRangePreset = (preset: { start: string; end: string }) => {
        setStartDate(preset.start);
        setEndDate(preset.end);
    };
    
    const getReportMutation = () => {
        switch (reportType) {
            case "daily_recon":
                return createDailyReconReport;
            case "pnl":
                return createPnlReport;
            case "equity":
                return createEquityReport;
            case "net_volume":
                return createNetVolumeReport;
        }
    };
    
    const getReportFileName = () => {
        const typeLabel = reportTypeOptions.find(opt => opt.value === reportType)?.label || reportType;
        const dateRange = isMonthlyReport 
            ? moment(selectedMonth, "YYYY-MM").format("MMMM-YYYY")
            : `${effectiveStartDate}_to_${effectiveEndDate}`;
        return `${typeLabel.replace(/\s+/g, "-")}_${dateRange}.xlsx`;
    };
    
    const handleGenerate = async () => {
        if (isGenerating) return;
        setIsGenerating(true);
        
        try {
            const mutation = getReportMutation();
            const result = await mutation.mutateAsync({
                start: effectiveStartDate,
                end: effectiveEndDate
            });
            
            if (!result.success || !result.data?.link) {
                console.error("Failed to generate report", result);
                toast({ 
                    title: "Report Generation Failed", 
                    description: result.message || "Failed to generate report.", 
                    variant: "destructive" 
                });
                return;
            }
            
            const link = result.data.link;
            downloadFromS3(link, getReportFileName());
            
            toast({ 
                title: "Report Generated", 
                description: "Your report has been generated and download started.", 
                variant: "default" 
            });
        } catch (error) {
            console.error("Failed to generate report", error);
            toast({ 
                title: "Report Generation Failed", 
                description: String(error), 
                variant: "destructive" 
            });
        } finally {
            setIsGenerating(false);
        }
    };
    
    return (
        <Dialog open={isOpen} onOpenChange={(open) => {
            if (!open && !isGenerating) {
                onClose();
            }
        }}>
            <DialogContent className="sm:max-w-[600px]">
                <DialogHeader>
                    <DialogTitle>Generate Brokerage Report</DialogTitle>
                    <DialogDescription>
                        Select report type and date range to generate a report
                    </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-6">
                    <div className="space-y-2 grid">
                        <Label>Report Type</Label>
                        <Combobox
                            value={reportType}
                            onValueChange={(value: string) => setReportType(value as ReportType)}
                            options={reportTypeOptions}
                            placeholder="Select report type"
                            dwimOnChange
                            isModal
                        />
                    </div>
                    
                    {isMonthlyReport ? (
                        <>
                            <div className="space-y-2">
                                <Label>Select Month</Label>
                                <Input
                                    type="month"
                                    value={selectedMonth}
                                    onChange={(e) => handleMonthChange(e.target.value)}
                                    max={today.format("YYYY-MM")}
                                />
                            </div>
                            
                            <div className="flex flex-wrap gap-2">
                                {monthPresets.map((preset) => (
                                    <Button
                                        key={preset.label}
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleMonthChange(preset.getValue())}
                                    >
                                        {preset.label}
                                    </Button>
                                ))}
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Start Date</Label>
                                    <Input
                                        type="date"
                                        value={startDate}
                                        onChange={(e) => setStartDate(e.target.value)}
                                        max={endDate || today.format("YYYY-MM-DD")}
                                    />
                                </div>
                                
                                <div className="space-y-2">
                                    <Label>End Date</Label>
                                    <Input
                                        type="date"
                                        value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                        min={startDate}
                                        max={today.format("YYYY-MM-DD")}
                                    />
                                </div>
                            </div>
                            
                            <div className="flex flex-wrap gap-2">
                                {dateRangePresets.map((preset) => (
                                    <Button
                                        key={preset.label}
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleDateRangePreset(preset.getValue())}
                                    >
                                        {preset.label}
                                    </Button>
                                ))}
                            </div>
                        </>
                    )}
                    
                    <div className="bg-muted p-3 rounded-md space-y-1">
                        <p className="text-sm font-medium">Selected Date Range:</p>
                        <p className="text-sm text-muted-foreground">
                            {moment(effectiveStartDate).format("MMMM D, YYYY")} - {moment(effectiveEndDate).format("MMMM D, YYYY")}
                        </p>
                    </div>
                    
                    <div className="flex justify-end gap-3">
                        <Button
                            variant="outline"
                            onClick={onClose}
                            disabled={isGenerating}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleGenerate}
                            disabled={isGenerating || !effectiveStartDate || !effectiveEndDate || !reportType || !moment(effectiveStartDate).isValid() || !moment(effectiveEndDate).isValid()}
                            className="flex items-center gap-2"
                        >
                            {isGenerating ? (
                                <>
                                    <Loader className="h-4 w-4 animate-spin" />
                                    Generating...
                                </>
                            ) : (
                                <>
                                    <Download className="h-4 w-4" />
                                    Generate Report
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
